(() => {
  if (window.splitShippingConsentInitialized) return;
  window.splitShippingConsentInitialized = true;

  const ATTRIBUTE_NAME = 'Split shipment consent';
  const NOTE_PATTERN = /\n?\[Split shipment consent: (?:Yes|No)\]/g;
  const CHECKOUT_SELECTOR = '#cart-checkout, #cart-sidebar-checkout, .cart-actions .button-checkout, .previewCartAction .button-checkout';
  const PANEL_CLASS = 'split-shipping-consent';
  let renderTimer;
  let renderSequence = 0;

  const sessionKey = (cartToken) => `split-shipping-consent:${cartToken}`;
  const cartUrl = () => `${window.routes?.root || '/'}cart.js`.replace('//cart.js', '/cart.js');
  const updateUrl = () => window.routes?.cart_update_url || `${window.routes?.root || '/'}cart/update.js`.replace('//cart/update.js', '/cart/update.js');

  async function getCart() {
    const separator = cartUrl().includes('?') ? '&' : '?';
    const response = await fetch(`${cartUrl()}${separator}_=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  function distinctVariantCount(cart) {
    return new Set(cart.items.map((item) => String(item.variant_id || item.id))).size;
  }

  function cartHasPreOrderItem(cart) {
    const hasPreOrderProperty = cart.items.some((item) => {
      return Object.entries(item.properties || {}).some(([name, value]) => {
        return /pre[\s_-]?order/i.test(`${name} ${value}`);
      });
    });
    return hasPreOrderProperty || Boolean(document.querySelector('[data-split-preorder="true"]'));
  }

  function noteWithConsent(note, value) {
    const customerNote = (note || '').replace(NOTE_PATTERN, '').trim();
    const consentNote = `[Split shipment consent: ${value}]`;
    return customerNote ? `${customerNote}\n${consentNote}` : consentNote;
  }

  function hideInternalNoteFromCustomer() {
    document.querySelectorAll('#cart-note, cart-note .text-area').forEach((input) => {
      const customerNote = (input.value || '').replace(NOTE_PATTERN, '').trim();
      if (input.value !== customerNote) input.value = customerNote;
    });
  }

  async function clearConsent(cart) {
    const note = (cart.note || '').replace(NOTE_PATTERN, '').trim();
    const response = await fetch(updateUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: { [ATTRIBUTE_NAME]: null },
        note
      })
    });
    if (!response.ok) throw new Error('Unable to clear split shipment consent');
  }

  async function saveConsent(value, preferredNote) {
    const cart = await getCart();
    const note = noteWithConsent(
      typeof preferredNote === 'string' ? preferredNote : cart.note,
      value
    );
    const response = await fetch(updateUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: { [ATTRIBUTE_NAME]: value },
        note
      })
    });

    if (!response.ok) throw new Error('Unable to save split shipment consent');
    return response.json();
  }

  function createPanel(location, selectedValue, cartToken) {
    const panel = document.createElement('fieldset');
    const groupName = `split-shipping-consent-${location}`;
    const instructionsId = `split-shipping-consent-instructions-${location}`;
    panel.className = PANEL_CLASS;
    panel.dataset.location = location;
    panel.innerHTML = `
      <legend class="split-shipping-consent__sr-only">Shipping Preference</legend>
      <div class="split-shipping-consent__header">
        <h3>Shipping Preference <span class="split-shipping-consent__required" aria-hidden="true">*</span><span class="split-shipping-consent__sr-only"> Required</span></h3>
      </div>
      <p class="split-shipping-consent__description">Can we ship your order separately if needed?</p>
      <p class="split-shipping-consent__sr-only" id="${instructionsId}">Use the arrow keys to change the selected shipping preference.</p>
      <div class="split-shipping-consent__choices">
        <label>
          <input type="radio" name="${groupName}" value="Yes" aria-describedby="${instructionsId}"${selectedValue === 'Yes' ? ' checked' : ''}>
          <span class="split-shipping-consent__option">
            <strong>Yes, separate shipments are okay</strong>
            <small>Items may arrive at different times.</small>
          </span>
        </label>
        <label>
          <input type="radio" name="${groupName}" value="No" aria-describedby="${instructionsId}"${selectedValue === 'No' ? ' checked' : ''}>
          <span class="split-shipping-consent__option">
            <strong>No, ship everything together</strong>
            <small>Wait until all items are ready to ship.</small>
          </span>
        </label>
      </div>
      <p class="split-shipping-consent__error" role="alert" hidden>Please select a shipping option before checkout.</p>
    `;

    panel.addEventListener('change', async (event) => {
      if (!event.target.matches('input[type="radio"]')) return;
      panel.querySelector('.split-shipping-consent__error').hidden = true;
      document.querySelectorAll(`.${PANEL_CLASS} input[value="${event.target.value}"]`).forEach((radio) => {
        radio.checked = true;
      });
      sessionStorage.setItem(sessionKey(cartToken), event.target.value);
      try {
        await saveConsent(event.target.value);
      } catch (error) {
        console.error(error);
      }
    });

    return panel;
  }

  function placePanel(button, location, selectedValue, cartToken) {
    const parent = button.parentElement;
    if (!parent) return;
    let panel = parent.querySelector(`:scope > .${PANEL_CLASS}`);
    if (!panel) {
      panel = createPanel(location, selectedValue, cartToken);
      parent.insertBefore(panel, button);
    }
    const selectedRadio = panel.querySelector(`input[value="${selectedValue}"]`);
    if (selectedRadio) selectedRadio.checked = true;
  }

  function panelForButton(button) {
    const actionArea = button.closest('.action-group, .previewCartGroup');
    const directPanel = actionArea?.querySelector(`.${PANEL_CLASS}`);
    if (directPanel) return directPanel;

    const cartArea = button.closest('.cart-actions, .previewCartAction');
    return cartArea?.querySelector(`.${PANEL_CLASS}`) || null;
  }

  async function render() {
    const buttons = document.querySelectorAll(CHECKOUT_SELECTOR);
    if (!buttons.length) return;
    const currentSequence = ++renderSequence;

    try {
      const cart = await getCart();
      if (currentSequence !== renderSequence) return;
      const shouldShow = distinctVariantCount(cart) >= 2 && cartHasPreOrderItem(cart);
      const savedValue = cart.attributes?.[ATTRIBUTE_NAME] || '';
      const sessionValue = sessionStorage.getItem(sessionKey(cart.token)) || '';
      const selectedValue = sessionValue === savedValue ? savedValue : '';
      hideInternalNoteFromCustomer();

      buttons.forEach((button) => {
        const location = button.id === 'cart-checkout' ? 'cart' : 'drawer';
        const existingPanel = button.parentElement?.querySelector(`:scope > .${PANEL_CLASS}`);
        if (!shouldShow) {
          existingPanel?.remove();
          return;
        }
        placePanel(button, location, selectedValue, cart.token);
      });

      if (shouldShow && savedValue && !selectedValue) {
        await clearConsent(cart);
      } else if (!shouldShow && savedValue) {
        sessionStorage.removeItem(sessionKey(cart.token));
        await clearConsent(cart);
      }

      const drawerItemCount = document.querySelectorAll('.previewCartItem').length;
      if (!shouldShow && drawerItemCount >= 2) {
        window.setTimeout(scheduleRender, 350);
      }
    } catch (error) {
      console.error(error);
    }
  }

  function scheduleRender() {
    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(render, 80);
  }

  function mutationAffectsCart(mutation) {
    const cartAreaSelector = '.previewCart, .previewCart-wrapper, cart-items, .cart-container';
    const target = mutation.target.nodeType === Node.ELEMENT_NODE
      ? mutation.target
      : mutation.target.parentElement;

    if (target?.closest?.(cartAreaSelector)) return true;

    return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return false;
      return node.matches?.(cartAreaSelector) || node.querySelector?.(cartAreaSelector);
    });
  }

  async function handleCheckout(event) {
    const checkoutButton = event.target.closest(CHECKOUT_SELECTOR);
    if (!checkoutButton) return;

    const panel = panelForButton(checkoutButton);
    if (!panel) return;

    const selected = panel.querySelector('input[type="radio"]:checked');
    if (!selected) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const error = panel.querySelector('.split-shipping-consent__error');
      error.hidden = false;
      panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      panel.querySelector('input[type="radio"]')?.focus();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutButton.disabled = true;

    try {
      const couponCode = document.getElementById('cart-coupon-code')?.value;
      if (couponCode) {
        localStorage.setItem('storedDiscount', couponCode);
        await fetch(`/discount/${encodeURIComponent(couponCode)}`);
      }

      const cartNoteInput = document.getElementById('cart-note');
      await saveConsent(selected.value, cartNoteInput?.value);
      window.location.assign(checkoutButton.dataset.href || '/checkout');
    } catch (error) {
      console.error(error);
      checkoutButton.disabled = false;
      const message = panel.querySelector('.split-shipping-consent__error');
      message.textContent = 'We could not save your choice. Please try again.';
      message.hidden = false;
    }
  }

  window.addEventListener('click', handleCheckout, true);

  window.addEventListener('submit', (event) => {
    const checkoutButton = event.submitter?.closest(CHECKOUT_SELECTOR);
    if (!checkoutButton || !panelForButton(checkoutButton)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutButton.click();
  }, true);

  const style = document.createElement('style');
  style.textContent = `
    .split-shipping-consent {
      background: #fff;
      border: 0;
      border-radius: 0;
      box-sizing: border-box;
      margin: 0 0 16px;
      padding: 0;
      text-align: left;
      width: 100%;
    }
    .split-shipping-consent__sr-only {
      clip: rect(0, 0, 0, 0);
      clip-path: inset(50%);
      height: 1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      white-space: nowrap;
      width: 1px;
    }
    .split-shipping-consent__header {
      align-items: center;
      display: flex;
      justify-content: space-between;
      margin: 0 0 7px;
      width: 100%;
    }
    .split-shipping-consent__header h3 {
      color: #1f1f1f;
      font-size: 17px;
      font-weight: 700;
      line-height: 1.25;
      margin: 0;
      padding: 0;
      text-transform: none;
    }
    .split-shipping-consent__required {
      color: #b42318;
      font-size: inherit;
      font-weight: 700;
      margin-left: 2px;
    }
    .split-shipping-consent__description {
      color: #555;
      font-size: 12px;
      line-height: 1.45;
      margin: 0 0 12px;
    }
    .split-shipping-consent__choices {
      border: 1px solid #d4d4d4;
      border-radius: 7px;
      display: flex;
      flex-direction: column;
      gap: 0;
      overflow: hidden;
    }
    .split-shipping-consent__choices label {
      align-items: center;
      background: #fff;
      border: 0;
      border-bottom: 1px solid #ddd;
      border-radius: 0;
      box-sizing: border-box;
      cursor: pointer;
      display: flex;
      font-size: 14px;
      font-weight: 400;
      gap: 11px;
      margin: 0;
      min-width: 0;
      padding: 12px;
      transition: background-color .15s ease, border-color .15s ease, color .15s ease;
      width: 100%;
    }
    .split-shipping-consent__choices label:last-child {
      border-bottom: 0;
    }
    .split-shipping-consent__choices input {
      appearance: none !important;
      -webkit-appearance: none !important;
      background: #fff;
      border: 2px solid #555 !important;
      border-radius: 50%;
      box-shadow: none !important;
      display: inline-block !important;
      flex: 0 0 20px;
      height: 20px;
      margin: 0;
      opacity: 1 !important;
      position: static !important;
      visibility: visible !important;
      width: 20px;
    }
    .split-shipping-consent__choices input:checked {
      background: radial-gradient(circle, #111 0 5px, #fff 6px);
      border-color: #111 !important;
    }
    .split-shipping-consent__choices label:has(input:checked) {
      background: #f8f8f8;
      color: #111;
    }
    .split-shipping-consent__choices label:has(input:checked) input {
      background: radial-gradient(circle, #111 0 5px, #fff 6px);
      border-color: #111 !important;
    }
    .split-shipping-consent__choices label:has(input:focus-visible) {
      background: #eef6ff;
      box-shadow: inset 0 0 0 3px #005fcc !important;
      position: relative;
      z-index: 1;
    }
    .split-shipping-consent__choices input:focus-visible {
      outline: 3px solid #005fcc !important;
      outline-offset: 3px !important;
    }
    .split-shipping-consent__option {
      display: flex;
      flex-direction: column;
      gap: 3px;
      line-height: 1.25;
    }
    .split-shipping-consent__option strong {
      color: #1f1f1f;
      font-size: 13px;
      font-weight: 700;
      text-transform: none !important;
    }
    .split-shipping-consent__option small {
      color: #666;
      font-size: 11px;
      font-weight: 400;
    }
    .split-shipping-consent__error {
      color: #b42318;
      font-size: 13px;
      font-weight: 600;
      line-height: 1.35;
      margin: 7px 0 0;
    }
    .previewCartAction .split-shipping-consent {
      margin-top: 4px;
      padding: 0;
    }
    .previewCartAction .split-shipping-consent__header h3 {
      font-size: 16px;
    }
  `;
  document.head.appendChild(style);

  new MutationObserver((mutations) => {
    if (mutations.some(mutationAffectsCart)) scheduleRender();
  }).observe(document.body, {
    childList: true,
    subtree: true
  });
  scheduleRender();
})();
